import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * Das Dokument in echt — nicht als Vorschaubild.
 *
 * Das Vorschaubild beantwortet „ist das die richtige Datei?". Sobald jemand darauf klickt, ist die
 * Frage eine andere: „was steht drin?". Dafür taugt ein 200 Pixel hohes Feld nicht, und die Datei
 * herunterzuladen und in einem anderen Programm zu öffnen ist ein Umweg für etwas, das der Browser
 * selbst kann.
 *
 * Bewusst ohne eigenes Laden: Die Datei liegt schon als Verweis vor, weil das Vorschaubild sie
 * geholt hat. Sie hier ein zweites Mal zu ziehen wäre derselbe Weg über die Leitung für dasselbe
 * Ergebnis — bei einem großen PDF ist das der Unterschied zwischen sofort und gleich.
 */

export interface DocumentViewerProps {
    /** Der Verweis auf die schon geladene Datei. Ohne ihn bleibt der Betrachter zu. */
    url?: string;
    /** Was oben steht — der Dateiname. */
    name: string;
    /** Womit gezeichnet wird. */
    kind: 'image' | 'pdf' | null;
    onClose: () => void;
}

export default function DocumentViewerDialog({ url, name, kind, onClose }: DocumentViewerProps) {
    const open = Boolean(url && kind);

    return (
        <Dialog open={open} onOpenChange={(next: boolean) => !next && onClose()}>
            <DialogContent className="flex h-[90vh] w-[min(1200px,95vw)] max-w-none flex-col gap-3">
                <DialogHeader className="shrink-0">
                    {/* Der volle Name, auch wenn er lang ist: Er ist die einzige Auskunft darüber,
                        welches Dokument hier offen steht. */}
                    <DialogTitle className="break-words pr-8 text-sm">{name}</DialogTitle>
                </DialogHeader>

                {/* Nimmt, was übrig bleibt. Eine feste Höhe hier hieße, dass der Betrachter auf
                    einem großen Bildschirm denselben Ausschnitt zeigt wie auf einem kleinen. */}
                <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-muted/30">
                    {kind === 'image' ? (
                        <img src={url} alt={name} className="h-full w-full object-contain" />
                    ) : kind === 'pdf' ? (
                        // Mit Bedienleiste, anders als das Vorschaubild: Hier wird gelesen, und dazu
                        // gehören Blättern, Zoom und Suche.
                        <iframe src={url} title={name} className="h-full w-full" />
                    ) : (
                        <></>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
